/**
 * Plan-of-care progress (2.5): how far along the plan is, by discipline group,
 * for the certification period — delivered against ordered, plus the period
 * boundary, the recert window and the day of the cert period.
 *
 * **Delivered does not depend on plan-visit binding.** `linkPlanVisits` binds a
 * plan visit to a real one by exact date, so a visit the seed's greedy fill moved
 * to another day never binds and any count that runs through the
 * binding reads low. This is the number that goes in a bar, so it is taken from
 * two sources that neither of them needs the binding, and the larger one wins:
 *
 *  - the tenant's own completed visits for this patient inside the cert period,
 *    on the days `meta.realDays` says the horizon is authoritative for;
 *  - the plan's own completed visits inside the cert period.
 *
 * The larger, never the sum: where the seed placed a visit on a different day
 * from the plan's, the two are the same care counted twice, so adding them would
 * double it. The horizon reaches fourteen days back, so for a patient a month
 * into an episode it can only ever see the tail — the plan carries the rest. And
 * because the calendar under the bars draws plan visits, the bar can never read
 * fewer delivered than the calendar draws with a check on it.
 *
 * Ordered is the plan's count for the cert period: one per plan visit of that
 * group that is not canceled, which is the same number the
 * track table prints under each discipline. A range frequency is already at the
 * plan's chosen count by the time it is a visit.
 *
 * The pace read is `selectOnPace` — the same selector the Act 3 guardrails use.
 * No new arithmetic.
 */
import type { ConfigState, DateKey, DemoState, Discipline, DisciplineGroup, Episode, PlanVisit, VisitTypeKind } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, daysBetweenKeys, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectOnPace, type OnPace } from "./lupa";
import { disciplineGroupFor } from "./roster";
import { dayOfCert, recertWindow } from "@caredna-marlin/utils/homeHealthDemo/engine/rules";

/**
 * The seed places a real visit on the clinician's shift, so a start of care whose
 * plan date falls on a weekend is delivered on the working day beside it. The
 * cert period's own start is widened by that much when the tenant's visits are
 * counted, and by no more, so a previous episode can never be read into this one.
 */
export const SOC_PLACEMENT_TOLERANCE_DAYS = 3;

export interface GroupProgress {
  group: DisciplineGroup;
  /** Plan visits of this group inside the cert period, canceled excluded. */
  ordered: number;
  /** Visits actually delivered (see the module note). */
  delivered: number;
  /** The tenant's own count, before the plan's is compared against it. */
  deliveredInTenant: number;
  /** Plotted and still to come: real or placeholder, from today on. */
  scheduled: number;
  /** Ordered but not yet plotted — the dashed "frequencies to follow" state. */
  pending: boolean;
  /** Weeks the branch read is carrying as an estimate while the orders are outstanding. */
  pendingWeeks?: number;
}

export interface PaymentPeriod {
  index: number;
  start: DateKey;
  end: DateKey;
}

export interface PlanProgress {
  episodeId: string;
  socDate: DateKey;
  certEndDate: DateKey;
  certPeriodDays: number;
  /** 1-based day of the cert period today, clamped to the period. */
  dayOfCert: number;
  periods: PaymentPeriod[];
  recert: { start: DateKey; end: DateKey };
  pace: OnPace;
  groups: GroupProgress[];
}

type ProgressState = Pick<DemoState, "episodes" | "planVisits" | "patients" | "config" | "meta" | "server">;

const ORDERED_STATUSES = new Set<PlanVisit["status"]>(["placeholder", "real", "completed", "missed"]);

/** Visit type id → the demo's visit-type kind, from the bindings the horizon resolved. */
function kindByVisitTypeId(state: Pick<DemoState, "meta">): Map<number, VisitTypeKind> {
  const out = new Map<number, VisitTypeKind>();
  for (const [kind, id] of Object.entries(state.meta.bindings.visitTypeIdsByKind)) {
    if (id !== undefined) out.set(id, kind as VisitTypeKind);
  }
  return out;
}

export function disciplineForVisitTypeKind(config: ConfigState, kind: VisitTypeKind): Discipline | undefined {
  return config.visitTypes[kind]?.disciplines[0];
}

/**
 * The tenant's completed visits for this patient inside the cert period, by
 * discipline group, on the days the horizon is authoritative for.
 */
export function deliveredFromHorizon(state: ProgressState, patientRef: string, episode: Episode): { byGroup: Map<DisciplineGroup, number>; days: Set<DateKey> } {
  const tz = state.config.branch.timeZone;
  const byGroup = new Map<DisciplineGroup, number>();
  const days = new Set(state.meta.realDays);
  const patientId = state.meta.bindings.patientIdsByRef[patientRef];
  if (patientId === undefined || days.size === 0) return { byGroup, days };
  const kinds = kindByVisitTypeId(state);
  const from = addDaysToKey(episode.socDate, -SOC_PLACEMENT_TOLERANCE_DAYS);
  const seen = new Set<number>();
  for (const vr of state.server.visitResources) {
    if (vr.patientId !== patientId || vr.eventStatus !== "complete" || seen.has(vr.visitId)) continue;
    const day = toDateKey(new Date(vr.startIso), tz);
    if (!days.has(day) || day < from || day > episode.certEndDate) continue;
    const kind = kinds.get(vr.visitTypeId);
    const discipline = kind ? disciplineForVisitTypeKind(state.config, kind) : undefined;
    if (!discipline) continue;
    seen.add(vr.visitId);
    const group = disciplineGroupFor(discipline);
    byGroup.set(group, (byGroup.get(group) ?? 0) + 1);
  }
  return { byGroup, days };
}

const GROUP_ORDER: DisciplineGroup[] = ["SN", "PT", "OT", "SLP", "MSW", "HHA"];

export function selectPlanProgress(state: ProgressState, patientRef: string, today: DateKey = toDateKey(selectDemoNow(state, new Date()), state.config.branch.timeZone)): PlanProgress | undefined {
  const episode = Object.values(state.episodes)
    .filter((e) => e.patientRef === patientRef)
    .sort((a, b) => b.socDate.localeCompare(a.socDate))[0];
  if (!episode) return undefined;

  const visits = Object.values(state.planVisits).filter((v) => v.episodeId === episode.id && v.targetDate >= episode.socDate && v.targetDate <= episode.certEndDate);
  const { byGroup: deliveredReal } = deliveredFromHorizon(state, patientRef, episode);

  const ordered = new Map<DisciplineGroup, number>();
  const scheduled = new Map<DisciplineGroup, number>();
  const deliveredPlan = new Map<DisciplineGroup, number>();
  for (const v of visits) {
    if (!ORDERED_STATUSES.has(v.status)) continue;
    const group = disciplineGroupFor(v.discipline);
    ordered.set(group, (ordered.get(group) ?? 0) + 1);
    if (v.status === "completed") {
      deliveredPlan.set(group, (deliveredPlan.get(group) ?? 0) + 1);
    } else if ((v.status === "real" || v.status === "placeholder") && v.targetDate >= today) {
      scheduled.set(group, (scheduled.get(group) ?? 0) + 1);
    }
  }

  const pending = new Map<DisciplineGroup, number>();
  for (const p of episode.pendingDisciplines) pending.set(disciplineGroupFor(p.discipline), p.weeks);

  const groups: GroupProgress[] = GROUP_ORDER.filter((g) => ordered.has(g) || pending.has(g)).map((group) => ({
    group,
    ordered: ordered.get(group) ?? 0,
    delivered: Math.max(deliveredReal.get(group) ?? 0, deliveredPlan.get(group) ?? 0),
    deliveredInTenant: deliveredReal.get(group) ?? 0,
    scheduled: scheduled.get(group) ?? 0,
    pending: pending.has(group),
    pendingWeeks: pending.get(group),
  }));

  const periodDays = state.config.paymentPeriodDays;
  const periods: PaymentPeriod[] = [];
  for (let start = episode.socDate, index = 0; start <= episode.certEndDate; start = addDaysToKey(start, periodDays), index += 1) {
    periods.push({ index, start, end: minKey(addDaysToKey(start, periodDays - 1), episode.certEndDate) });
  }

  const day = dayOfCert(episode, today);
  return {
    episodeId: episode.id,
    socDate: episode.socDate,
    certEndDate: episode.certEndDate,
    certPeriodDays: state.config.certPeriodDays,
    dayOfCert: Math.min(Math.max(day, 1), state.config.certPeriodDays),
    periods,
    recert: recertWindow(episode),
    pace: selectOnPace(Object.values(state.planVisits).filter((v) => v.episodeId === episode.id), episode, state.config, today),
    groups,
  };
}

function minKey(a: DateKey, b: DateKey): DateKey {
  return a < b ? a : b;
}

/** Where today sits on the cert-period line, 0 to 1 — the tick the progress panel draws. */
export function certPeriodFraction(progress: Pick<PlanProgress, "socDate" | "certPeriodDays">, date: DateKey): number {
  const elapsed = daysBetweenKeys(progress.socDate, date);
  return Math.min(Math.max(elapsed / Math.max(progress.certPeriodDays - 1, 1), 0), 1);
}
